import {StyleSheet, css} from 'aphrodite';
import React, {ReactNode} from 'react';

import {FONT_PROXIMA_15, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';

import {GRAY_DARKER} from './Colors';

/** @package */
export interface UIPlaceholderProps {
  className?: string;
  infoSection: ReactNode;
  /**
   * Optional element to render underneath the info section; should be used for rendering an actionable element
   *  (e.g. a Button).
   */
  actionContent?: JSX.Element;
}

/**
 * A placeholder component to fill a container with information and an optional actionable element.
 *
 * This component should be the child of a large blank area, and will position itself within that area.
 *
 * @storybook https://dev--622a4c57262ed1003a5a57fa.chromatic.com/?path=/story/ui-library-uiplaceholder--basic
 *
 * For example, you can inform the user that there are no items on the page and have an "Add item" button
 * underneath the notice:
 *
 * @example
 * <UIPlaceholder
 *   infoText="There are no items on the page"
 *   actionContent={<Button text="Add item" ... />}
 * />
 */
export default function UIPlaceholder({infoSection, actionContent}: UIPlaceholderProps): JSX.Element {
  return (
    <div className={css(styles.wrapper)}>
      <div className={css(styles.infoSection)}>{infoSection}</div>
      {actionContent}
    </div>
  );
}

export const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    top: '25%',
  },
  infoSection: {
    ...FONT_PROXIMA_15,
    color: GRAY_DARKER,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    textAlign: 'center',
    width: 320,
    marginBottom: 12,
  },
});
